import { Box, Divider, Stack, Typography } from "@mui/material";
import { FC, useState } from "react";

import __ from "web/Layout/Translations";

import {
  FormView,
  HandleToggleReceiversModalFn,
  IDuplicateCardData,
  ISubscriptionReceiver,
  SubscriptionCheckoutSteps,
  SubscriptionCollectedDataScope,
  SubscriptionFormReceiver,
} from "web/types/Subscription";
import { Nullable } from "web/types/Utils";

import SubscriptionReceiverMUI from "./SubscriptionReceiver/subscriptionReceiverMUI";

interface ISubscriptionReceiversProps {
  receivers: ISubscriptionReceiver[];
  wayChooseOthersInActiveForm: SubscriptionFormReceiver;
  subscriptionId: string;
  viewType: FormView;
  configuratorStep?: SubscriptionCheckoutSteps;
  enableOrderDuplicateCard?: boolean;
  benefitStatus: string;
  duplicateCardData?: Nullable<IDuplicateCardData>;
  duplicateTrigger?: boolean;
  handleToggleReceiversModal?: HandleToggleReceiversModalFn;
  updateReceiversDataFunc?: (newReceiverData: ISubscriptionReceiver) => void;
  name: string;
  groupName: string;
  collectedDataScope?: SubscriptionCollectedDataScope;
  benefitGroupType?: number | undefined;
}

const SubscriptionReceiversMUI: FC<ISubscriptionReceiversProps> = ({
  receivers,
  wayChooseOthersInActiveForm,
  subscriptionId,
  viewType = FormView.CONFIGURATOR,
  configuratorStep = SubscriptionCheckoutSteps.INFORMATIONS,
  duplicateCardData,
  duplicateTrigger = false,
  enableOrderDuplicateCard = false,
  benefitStatus,
  handleToggleReceiversModal,
  updateReceiversDataFunc,
  name,
  groupName,
  collectedDataScope,
  benefitGroupType,
}) => {
  const [openDuplicateForm, setOpenDuplicateForm] =
    useState<Nullable<string>>(null);
  const employeeReceiver = receivers.find(
    (receiver) => receiver.type === SubscriptionFormReceiver.EMPLOYEE
  );
  const otherReceivers = receivers.filter(
    (receiver) => receiver.type === SubscriptionFormReceiver.OTHERS
  );
  // Check for unsubmitted forms - only on configurator view; on My Subscriptions view all forms are submitted by default
  const unsubmittedFormsAvailable =
    viewType === FormView.CONFIGURATOR &&
    configuratorStep === SubscriptionCheckoutSteps.INFORMATIONS &&
    !!otherReceivers.find(({ form }) => !form?.submitted);
  const formsAwaitingUpdateAvailable = !!otherReceivers.find(
    ({ form }) => form?.awaitsUpdate
  );

  if (collectedDataScope === SubscriptionCollectedDataScope.NULL) {
    return (
      <Typography
        variant="subtitle2"
        sx={{
          px:
            configuratorStep === SubscriptionCheckoutSteps.INFORMATIONS
              ? {
                  xs: 2,
                  md: 3,
                }
              : {},
          pt: 2,
          pb: 3,
        }}
      >
        {__("Nie wymaga podania danych")}
      </Typography>
    );
  }

  return (
    <Box
      sx={{
        px: viewType === FormView.SUMMARY_STEP ? 0 : { xs: 2, md: 3 },
        py: 2,
      }}
    >
      {employeeReceiver && (
        <>
          <Typography mb={2} variant="subtitle1">
            {__("Twoje dane")}
          </Typography>
          <SubscriptionReceiverMUI
            data={employeeReceiver}
            subscriptionId={subscriptionId}
            viewType={viewType}
            configuratorStep={configuratorStep}
            benefitStatus={benefitStatus}
            // DUPLICATE PROPS
            enableOrderDuplicateCard={enableOrderDuplicateCard}
            duplicateCardData={duplicateCardData}
            updateReceiversDataFunc={updateReceiversDataFunc}
            duplicateTrigger={duplicateTrigger}
            handleToggleReceiversModal={handleToggleReceiversModal}
            setOpenDuplicateForm={setOpenDuplicateForm}
            openDuplicateForm={openDuplicateForm}
            name={name}
            groupName={groupName}
            benefitGroupType={benefitGroupType}
          />
        </>
      )}
      {!!otherReceivers.length && (
        <Box mt={employeeReceiver ? 2 : 0}>
          {employeeReceiver && <Divider sx={{ my: 2 }} />}
          <Typography variant="subtitle1">
            {__("Dane osób, dla których wybierasz abonament")}
          </Typography>
          {(unsubmittedFormsAvailable || formsAwaitingUpdateAvailable) &&
            wayChooseOthersInActiveForm !== SubscriptionFormReceiver.OTHERS && (
              <Typography variant="body2">
                {__("Uzupełnij dane osób dodatkowych.")}
              </Typography>
            )}
          {wayChooseOthersInActiveForm === SubscriptionFormReceiver.OTHERS &&
            viewType !== "summaryStep" && (
              <Typography variant="body2">
                {__("Na podany e-mail zostanie wysłany formularz.")}
              </Typography>
            )}
          <Stack mt={2}>
            {otherReceivers.map((receiver, index) => (
              <Box key={receiver.id}>
                <SubscriptionReceiverMUI
                  data={receiver}
                  index={employeeReceiver ? index + 1 : index}
                  subscriptionId={subscriptionId}
                  wayChooseOthersInActiveForm={wayChooseOthersInActiveForm}
                  viewType={viewType}
                  configuratorStep={configuratorStep}
                  benefitStatus={benefitStatus}
                  // DUPLICATE PROPS
                  enableOrderDuplicateCard={enableOrderDuplicateCard}
                  duplicateCardData={duplicateCardData}
                  updateReceiversDataFunc={updateReceiversDataFunc}
                  duplicateTrigger={duplicateTrigger}
                  handleToggleReceiversModal={handleToggleReceiversModal}
                  setOpenDuplicateForm={setOpenDuplicateForm}
                  openDuplicateForm={openDuplicateForm}
                  name={name}
                  groupName={groupName}
                  benefitGroupType={benefitGroupType}
                />
                {index !== otherReceivers.length - 1 && (
                  <Divider sx={{ my: 2 }} />
                )}
              </Box>
            ))}
          </Stack>
        </Box>
      )}
    </Box>
  );
};

export default SubscriptionReceiversMUI;
